import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "../../lib/api";
import { Spinner } from "../Bits";
import ImageUpload from "../ImageUpload";
import { Sparkles, Image as ImageIcon, Save } from "lucide-react";

// All editable menu pages
const PAGES = [
  { slug: "plants", label: "Pflanzen-Datenbank" },
  { slug: "identify", label: "Erkennen" },
  { slug: "tours", label: "Touren" },
  { slug: "season", label: "Saisonkalender" },
  { slug: "recipes", label: "Rezepte" },
  { slug: "quiz", label: "Quiz" },
  { slug: "compare", label: "Vergleichen" },
  { slug: "journal", label: "Tagebuch" },
  { slug: "favorites", label: "Merkliste" },
  { slug: "magazin", label: "Magazin" },
  { slug: "contact", label: "Kontakt" },
  { slug: "coach", label: "KI-Coach" },
  { slug: "community", label: "Community" },
];

export default function PagesAdmin() {
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState("plants");
  const [imgBusy, setImgBusy] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/admin/settings").then((r) => setSettings(r.data || {})).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const k = (suffix) => `page_${active}_hero_${suffix}`;
  const set = (suffix, val) => setSettings((s) => ({ ...s, [k(suffix)]: val }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        [k("image")]: settings[k("image")] || "",
        [k("overline")]: settings[k("overline")] || "",
        [k("title")]: settings[k("title")] || "",
        [k("subtitle")]: settings[k("subtitle")] || "",
      };
      await api.put("/admin/settings", payload);
      toast.success(`«${PAGES.find((p) => p.slug === active)?.label}" gespeichert`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Speichern fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  };

  const aiImage = async () => {
    const title = settings[k("title")] || PAGES.find((p) => p.slug === active)?.label;
    setImgBusy(true);
    try {
      const r = await api.post("/admin/articles/ai-image", {
        prompt: `Atmospheric photorealistic nature shot for the page "${title}" of a Luxembourg wild plant magazine. Warm natural light, earthy tones, no humans, no text.`,
        style: "photo",
      });
      set("image", r.data.url);
      toast.success("KI-Bild generiert (noch nicht gespeichert)");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bild-Fehler");
    } finally {
      setImgBusy(false);
    }
  };

  if (loading) return <Spinner />;

  const page = PAGES.find((p) => p.slug === active);

  return (
    <div data-testid="admin-pages">
      <div className="flex items-start gap-2 flex-wrap mb-6">
        {PAGES.map((p) => (
          <button
            key={p.slug}
            onClick={() => setActive(p.slug)}
            className="text-xs px-3 py-1.5 rounded-full transition-colors"
            style={{
              backgroundColor: active === p.slug ? "var(--wp-olive)" : "var(--wp-cream)",
              color: active === p.slug ? "var(--wp-cream)" : "var(--wp-forest)",
              border: `1px solid ${active === p.slug ? "var(--wp-olive)" : "var(--wp-border)"}`,
            }}
            data-testid={`pages-tab-${p.slug}`}
          >
            {p.label}
          </button>
        ))}
      </div>

      <div className="plant-card p-6">
        <div className="flex items-start justify-between gap-4 mb-4">
          <div>
            <div className="overline">Seite</div>
            <h3 className="font-display text-2xl" style={{ color: "var(--wp-olive)" }}>{page?.label}</h3>
            <div className="text-xs opacity-60 mt-1">URL: /{page?.slug}</div>
          </div>
          <button
            onClick={save}
            disabled={saving}
            className="btn-primary !py-2 !px-4"
            data-testid="pages-save"
          >
            <Save className="w-4 h-4" />
            {saving ? "Speichere …" : "Speichern"}
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {/* Image */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="overline">Hero-Bild</div>
              <button
                type="button"
                onClick={aiImage}
                disabled={imgBusy}
                className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full"
                style={{ backgroundColor: "var(--wp-sand)", color: "var(--wp-forest)", opacity: imgBusy ? 0.6 : 1 }}
                data-testid="pages-ai-image"
              >
                <Sparkles className="w-3.5 h-3.5" /> {imgBusy ? "Generiere …" : "KI-Bild"}
              </button>
            </div>
            <div className="space-y-2">
              <ImageUpload value={settings[k("image")]} onChange={(v) => set("image", v)} endpoint="/admin/uploads" testId={`pages-image-upload-${active}`} />
              <input
                placeholder="oder Bild-URL"
                value={settings[k("image")] || ""}
                onChange={(e) => set("image", e.target.value)}
                className="w-full px-3 py-2 rounded-lg border text-xs"
                style={{ borderColor: "var(--wp-border)" }}
                data-testid={`pages-image-url-${active}`}
              />
            </div>
            {settings[k("image")] && (
              <div className="mt-3 rounded-lg overflow-hidden border" style={{ borderColor: "var(--wp-border)" }}>
                <img src={settings[k("image")]} alt="Hero preview" className="w-full h-40 object-cover" />
              </div>
            )}
          </div>

          {/* Texts */}
          <div className="space-y-3">
            <label className="block">
              <div className="overline mb-1">Overline (kleine Beschriftung oben)</div>
              <input
                value={settings[k("overline")] || ""}
                onChange={(e) => set("overline", e.target.value)}
                className="w-full px-3 py-2 rounded-lg border text-sm"
                style={{ borderColor: "var(--wp-border)" }}
                data-testid={`pages-overline-${active}`}
              />
            </label>
            <label className="block">
              <div className="overline mb-1">Hauptüberschrift</div>
              <input
                value={settings[k("title")] || ""}
                onChange={(e) => set("title", e.target.value)}
                className="w-full px-3 py-2 rounded-lg border text-sm font-display"
                style={{ borderColor: "var(--wp-border)" }}
                data-testid={`pages-title-${active}`}
              />
            </label>
            <label className="block">
              <div className="overline mb-1">Untertitel / Beschreibung</div>
              <textarea
                value={settings[k("subtitle")] || ""}
                onChange={(e) => set("subtitle", e.target.value)}
                rows={5}
                className="w-full px-3 py-2 rounded-lg border text-sm"
                style={{ borderColor: "var(--wp-border)" }}
                data-testid={`pages-subtitle-${active}`}
              />
            </label>
          </div>
        </div>

        <p className="text-xs opacity-60 mt-5">
          <ImageIcon className="w-3.5 h-3.5 inline mr-1.5" />
          Änderungen werden sofort auf der jeweiligen Menü-Seite sichtbar, sobald du auf «Speichern" klickst.
        </p>
      </div>
    </div>
  );
}
